// src/layouts/trafficmonitoring/routeFlows/routeFlowsApiDate.js
import axios from "axios";
import i18n from "i18n";
import config from "../../../config";

const API_URL = config.TRAFFIC; // e.g., https://sora.imet.gr/api/rpc

/** ------------------ helpers ------------------ **/

// Converts "YYYY-MM-DD" to ISO UTC at the END of the day (23:59:59.999 local -> UTC)
function endOfDayUTCISO(dateISO) {
  if (!dateISO) return new Date().toISOString(); // last 24h: now
  const [y, m, d] = dateISO.split("-").map(Number);
  const localEnd = new Date(y, m - 1, d, 23, 59, 59, 999);
  // Shift to UTC ISO without changing the actual moment in time
  return new Date(
    localEnd.getTime() - localEnd.getTimezoneOffset() * 60000
  ).toISOString();
}

// Builds URL with query ?in_timestamp=
function buildUrl(base, endpoint, inTimestampISO) {
  const url = new URL(`${base}${endpoint}`);
  if (inTimestampISO) url.searchParams.set("in_timestamp", inTimestampISO);
  return url.toString();
}

// Gentle shades based on magnitude
function generateShades(
  values,
  { hue = 200, sat = 70, lightMin = 30, lightMax = 80 } = {}
) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return values.map((v) => {
    const norm = max === min ? 0.5 : (v - min) / (max - min);
    const lightness = lightMax - norm * (lightMax - lightMin);
    return `hsl(${hue}, ${sat}%, ${lightness}%)`;
  });
}

// Legend with fixed box colors + v3-safe toggle
function buildLegendConfig({
  actualColor = "#0095b3",
  typicalColor = "#CE2452",
} = {}) {
  return {
    onClick: (e, legendItem, legend) => {
      const chart = legend.chart;
      const i = legendItem?.datasetIndex;
      if (i == null) return;
      const meta = chart.getDatasetMeta(i);
      if (!meta) return;
      meta.hidden =
        meta.hidden === null ? !chart.data.datasets[i].hidden : null;
      chart.update();
    },
    labels: {
      generateLabels: function (chart) {
        return chart.data.datasets.map((ds, i) => ({
          text: ds.label,
          fillStyle: i === 0 ? actualColor : typicalColor,
          lineWidth: 1,
          hidden: !chart.isDatasetVisible(i),
          index: i,
        }));
      },
    },
  };
}

// Compose a title if not explicitly provided
function inferTitle({ road, direction, title }) {
  if (title) return title;
  const roadName =
    road === "egnatia"
      ? "Egnatia"
      : road === "olgas"
      ? "Olgas"
      : road === "nikis"
      ? "Nikis"
      : road;
  const dir = direction === "in" ? "In" : direction === "out" ? "Out" : "";
  return dir ? `${roadName} ${dir}` : roadName;
}

/** ------------------ core API ------------------ **/

/**
 * Fetches flows for ONE entry from schemaDate:
 * - Calls a single endpoint (e.g., /get_route_flows_olgas)
 * - Reads actual/typical from valueKey/typicalKey (or infers from direction)
 * - Sorts with sort (asc|desc)
 * - Returns a Chart.js-ready object with 2 datasets (Actual & Typical)
 *
 * @param {{
 *   endPoint: string,
 *   road?: 'egnatia'|'olgas'|'nikis',
 *   direction?: 'in'|'out',
 *   title?: string,
 *   valueKey?: string,
 *   typicalKey?: string,
 *   sort?: 'asc'|'desc'
 * }} schemaEntry
 * @param {{ date?: string|null, inTimestamp?: string|null, signal?: AbortSignal }} options
 * @returns {Promise<{ id: string|number, data: { labels: string[], datasets: any[] }, options: object }>}
 */
export async function fetchRouteFlowsForDate(schemaEntry, options = {}) {
  const {
    endPoint,
    road,
    direction,
    title,
    valueKey: vkFromSchema,
    typicalKey: tkFromSchema,
    sort = "asc",
    id,
  } = schemaEntry;

  const { date = null, inTimestamp = null, signal } = options;

  // 1) Timestamp: inTimestamp has priority; otherwise from date → endOfDayUTCISO
  const ts = inTimestamp || endOfDayUTCISO(date);

  // 2) Determine value/typical keys
  const valueKey =
    vkFromSchema ||
    (direction === "in"
      ? "value_in"
      : direction === "out"
      ? "value_out"
      : "value_in");
  const typicalKey =
    tkFromSchema ||
    (direction === "in"
      ? "typical_value_in"
      : direction === "out"
      ? "typical_value_out"
      : "typical_value_in");

  // 3) API call
  const url = buildUrl(API_URL, endPoint, ts);
  const res = await axios.get(url, {
    headers: { "Accept-Profile": "traffic_monitoring" },
    signal,
  });

  let rows = Array.isArray(res.data) ? res.data.slice() : [];

  // 4) Sorting (by row id)
  rows.sort((a, b) => {
    const cmp = (a.id ?? 0) - (b.id ?? 0);
    return sort === "desc" ? -cmp : cmp;
  });

  // 5) Labels & values
  const labels = rows.map((r) => r.name);
  const actualValues = rows.map((r) =>
    Number.isFinite(r?.[valueKey]) ? r[valueKey] : 0
  );
  const typicalValues = rows.map((r) =>
    Number.isFinite(r?.[typicalKey]) ? r[typicalKey] : 0
  );

  // 6) Colors (different palettes for Actual/Typical)
  const actualColors = generateShades(actualValues, {
    hue: 190,
    sat: 70,
    lightMin: 30,
    lightMax: 75,
  });
  const typicalColors = generateShades(typicalValues, {
    hue: 344,
    sat: 70,
    lightMin: 40,
    lightMax: 75,
  });

  const datasets = [
    {
      label: i18n.t("trafficMonitoring.routeFlows.actual"),
      data: actualValues,
      backgroundColor: actualColors,
      maxBarThickness: 30,
      borderWidth: 0,
    },
    {
      label: i18n.t("trafficMonitoring.routeFlows.typical"),
      data: typicalValues,
      backgroundColor: typicalColors,
      maxBarThickness: 30,
      borderWidth: 0,
    },
  ];

  // 7) Base options (you’ll apply global yMax later with enrichChartOptions)
  const baseTitle = inferTitle({ road, direction, title });
  const optionsOut = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, ...buildLegendConfig() },
      title: {
        display: true,
        text: baseTitle,
        font: { size: 16 },
        color: "#fff",
      },
      datalabels: {
        anchor: "end",
        align: "end",
        offset: -4,
        formatter: (v) => v ?? "",
        font: { size: 12 },
        color: "#888a91",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { display: false },
        grid: { display: false },
      },
    },
  };

  return {
    id: id ?? `${road || endPoint}-${direction || "dir"}`,
    data: { labels, datasets },
    options: optionsOut,
  };
}

/**
 * Helper: fetches ALL charts from schemaDate for a given date
 * and returns an array of Chart.js-ready objects.
 *
 * @param {Array} schemaDate
 * @param {{ date?: string|null, inTimestamp?: string|null, signal?: AbortSignal }} options
 */
export async function fetchAllRouteFlowsForDate(schemaDate, options = {}) {
  console.log("fetchAllRouteFlowsForDate", schemaDate, options);
  const res = await Promise.all(
    (schemaDate || []).map((entry) =>
      fetchRouteFlowsForDate(entry, options).catch((err) => {
        if (err?.name !== "AbortError" && err?.name !== "CanceledError") {
          console.error(
            `fetchRouteFlowsForDate failed for ${
              entry?.title || entry?.endPoint
            }`,
            err
          );
        }
        return null;
      })
    )
  );
  return res.filter(Boolean);
}
